import React from 'react';
import { Box, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { NAVY, headCell, bodyCell } from './widgets.comp';

/*
 * "How Learning Impact Is Measured" — static methodology content shared by the
 * Network, School, Partner and National dashboards. The measurement methodology
 * is not dashboard-scoped, so the wording (title, subtitle, metric cards and the
 * signal rubric) lives here once. The header itself is drawn by the calling
 * dashboard's own SectionHeader, passed in as a prop, so it keeps that
 * dashboard's header style.
 */

const GREEN = '#1B6B55';

const SIGNAL_RUBRIC = [
    {signal: 'Early',    rule: 'Impact below 10%, gain below 3 points, or valid completion below 50%.', meaning: 'Small improvement or more results needed. Support learners and collect more evidence.'},
    {signal: 'Moderate', rule: 'Impact 10% to below 30%, gain ≥3 points, and valid completion ≥50%.',   meaning: 'Positive learning progress. Reinforce areas needing practice.'},
    {signal: 'Strong',   rule: 'Impact ≥30%, gain ≥3 points, and valid completion ≥50%.',               meaning: 'Substantial learning progress. Follow up with a COS reassessment.'},
];

const IMPACT_METRICS = [
    {
        title: 'Impact Rate',
        body: 'How much of the possible remaining improvement learners achieved.',
        formula: 'Improvement Rate = (Post − Pre) ÷ (100 − Pre) × 100',
    },
    {
        title: 'Learning Impact Signal',
        body: 'The strength of the learning evidence linked back to the COS area or item.',
        formula: 'Use this as a learning-linked progress signal only.',
    },
];

const MetricCard = ({title, body, formula}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
    }}>
        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1}}>{title}</Typography>
        <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6, mb: 1.5}}>{body}</Typography>
        <Box sx={{
            borderLeft: `3px solid ${GREEN}`, backgroundColor: 'var(--brand-surface-muted, #F3F4F6)',
            borderRadius: '6px', p: 1.5,
        }}>
            <Typography sx={{fontSize: '12.5px', color: '#374151', lineHeight: 1.6, fontFamily: 'monospace'}}>{formula}</Typography>
        </Box>
    </Box>
);

export const LearningImpactMethodologySection = ({SectionHeader}) => (
    <Box>
        <SectionHeader
            title="How Learning Impact Is Measured"
            subtitle="Learning impact tracks program impact and the strength of learning evidence."
        />

        <Grid container spacing={3} alignItems="stretch" sx={{mb: 4}}>
            {IMPACT_METRICS.map((m) => (
                <Grid item xs={12} md={6} key={m.title}>
                    <MetricCard {...m}/>
                </Grid>
            ))}
        </Grid>

        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            Learning Impact Signal Rubric
        </Typography>
        <TableContainer sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', mb: 1,
        }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{...headCell, textAlign: 'left', pl: 2.5, width: '18%'}}>Signal</TableCell>
                        <TableCell sx={{...headCell, textAlign: 'left', width: '35%'}}>Rule</TableCell>
                        <TableCell sx={{...headCell, textAlign: 'left'}}>Meaning and next step</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {SIGNAL_RUBRIC.map((row, i) => (
                        <TableRow key={row.signal} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                            <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.signal}</TableCell>
                            <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.rule}</TableCell>
                            <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.meaning}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);
